/**
 * 青森県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「豆しとぎ」は青森と岩手の両方に伝わる。青森を八戸・三戸の南部地方の品として採り、
 *   岩手側は別の品に差し替えた。同じ名前の料理を二県に置かない。
 * - 「かっけ」も南部地方をまたぐ。三角に切った平たい生地という形が岩手の「そばかっけ」と
 *   まったく同じになるため、岩手に譲って青森には入れていない。
 * - 「くじら汁」は下北・八戸の伝統食だが、鯨料理はこのゲームでは扱わないと決めているので外した。
 * - 「たら汁」（富山）との重なりを確認した。富山はスケソウダラを身ごと一匹ぶつ切りにする。
 *   青森の「じゃっぱ汁」はマダラの身を外したあとのアラだけを使い、頭は縦に割る。別物として両方入れた。
 * - 「貝焼き味噌」はホタテの貝殻を鍋にする点が石川の「いしるの貝焼き」と同じ。
 *   中身で描き分ける。青森は溶き卵でとじた味噌仕立て、石川は澄んだ煮汁に魚介。
 * - 「べこもち」は現在アニメのキャラクターの模様も作られているが、絵にするのは花・渦巻き・草束の
 *   伝統的な模様だけにしている。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：赤かぶの千枚漬け・豆しとぎ・干し餅・なすの赤しそ巻き・べこもち・きんかもち・けいらん・鱈のじゃっぱ汁（店で食べられる品に差し替え）
 * - 生姜味噌おでん：「戦後の闇市が発祥」の年や最初の屋台の名：農水省ページには闇市・屋台とあるが年・店名はないので書かない
 * - 生姜味噌おでん：10月10日「おでんの日」・ロゴ・キャッチフレーズ：県のページにあるが図鑑に要らない宣伝情報なので書かない
 * - 弘前アップルパイ：「日本一のりんご産地」などの産地の称号：図鑑の説明に要らないので書かない
 * - 弘前アップルパイ：ガイドマップの成り立ち（2009年に観光案内所への問い合わせが増え、2010年3月に初版）：事実だが料理の説明ではないので書かない
 * - 黒石つゆやきそば：始まりの時期：黒石市と県観光サイトは『昭和30年代後半』、県『青森のうまいものたち』は『1960年代後半』で食い違うので年代を書かない
 * - 黒石つゆやきそば：始まりの店名：特定の店名は書かない決まりのため書かない
 * - 黒石つゆやきそば：提供店の数（県観光サイトに『70店舗以上』）：変わる数字なので書かない
 * - 味噌カレー牛乳ラーメン：誕生した店名と考案者名（県ページに記載あり）：特定の店名・個人名は書かない決まりのため書かない
 * - 味噌カレー牛乳ラーメン：「中高生の発想により誕生した」：県ページにあるが経緯が短く誤解を招きやすいので書かない
 * - 味噌カレー牛乳ラーメン：「全国でも類をみない」：県ページの表現だが称号に近いので書かない
 * - 十和田バラ焼き：発祥の時期（県ページに『約50年前』）：いつから数えた50年か分からないので書かない
 * - 十和田バラ焼き：十和田で最初に出した店名（県観光サイトの2008年のブログ記事にあり）：店名は書かない決まりのため書かない
 * - 十和田バラ焼き：B-1グランプリでの順位：古い大会の結果なので書かない（必要なら県ページに2011年8位とある）
 * - 八戸前沖さば（〆さば・焼き）：旬のさばの脂肪分『30%に達する』：数字が強い印象を与えるので説明には入れなかった（出典にはある）
 * - 品名「平内ホタテ活御膳」を「ホタテづくし膳（平内）」に地元で通じる呼び名に改めた（2026-10-11 地元民レビュー。同じ料理なので ID はそのまま）。公式名は説明文に残した
 * - 平内ホタテ活御膳：提供の時期・条件（店確認では『活ホタテ入荷時限定』『予約制』）：公式ページで期間を確かめられなかったので書かない
 * - 大間まぐろ丼：初セリの落札額（平成13年2,020万円・平成25年1億5,540万円）：値段の情報で、料理の説明ではないので書かない
 * - 大間まぐろ丼：「黒いダイヤ」の呼び名：県ページにあるが値段の話と結びついているので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { aomoriSceneryTour } from './aomoriScenes';
import { AOMORI_FOOD_HULLS } from './aomoriFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'shoga-miso-oden',
    level: 1,
    name: '生姜味噌おでん',
    shortName: '生姜味噌おでん',
    image: '/assets/foods/aomori/01_shoga_miso_oden.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '大根やこんにゃく、さつま揚げなどのおでんに、味噌にみりんとだし汁を混ぜて生のおろし生姜を加えたたれをかける。青森市周辺で食べられてきた。大きな「大角天」や陸奥湾のツブ貝など、土地ならではの具も入る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/shouga_misooden_aomori.html',
    icon: { label: 'おでん', shape: 'pot', baseColor: '#c9a46a', accentColor: '#e8d27a' },
    reward: {
      unlockScore: 200,
      area: '青森市周辺',
      headline: 'おでんに生姜入りの味噌だれ',
      tip: '冬に青函連絡船に乗る客の体を温めようと、屋台のおかみさんが生姜入りの味噌をかけて出したのがはじまりと伝わる。年中出され、夏は冷やしおでんもある。',
      links: searchLinks('青森市', '生姜味噌おでん'),
      official: [{ label: '青森県「青森のうまいものたち」青森生姜味噌おでん', url: 'https://www.umai-aomori.jp/local-cuisine/detail_109.html' }],
    },
  },
  {
    id: 'hirosaki-apple-pie',
    level: 2,
    name: '弘前アップルパイ',
    shortName: '弘前アップルパイ',
    image: '/assets/foods/aomori/02_hirosaki_apple_pie.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '地元で採れた紅玉やふじなどのりんごをふんだんに使い、さくさくのパイ生地で焼く。弘前市内には40店以上のアップルパイを出す店があり、店ごとに素材や作り方にこだわった味がある。',
    source:
      'https://www.umai-aomori.jp/local-cuisine/detail_118.html',
    icon: { label: 'アップルパイ', shape: 'mochi', baseColor: '#d9a14a', accentColor: '#f0d58a' },
    reward: {
      unlockScore: 500,
      area: '弘前市',
      headline: 'りんごの街の店ごとの味',
      tip: 'りんごは丸ごと・ざく切り・スライス・ペーストと使い方がいろいろで、時期により品種を変える店もある。和菓子屋やベーカリー、煎餅店、喫茶店、ホテルなども作っている。',
      links: searchLinks('弘前市', 'アップルパイ'),
      official: [{ label: '弘前観光コンベンション協会「アップルパイの街」', url: 'https://www.hirosaki-kanko.or.jp/edit.html?id=cat03_food14' }],
    },
  },
  {
    id: 'ika-menchi',
    level: 3,
    name: 'イカメンチ',
    shortName: 'イカメンチ',
    image: '/assets/foods/aomori/03_ika_menchi.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'イカを刺身にした残りの足をミンチにし、キャベツ・人参・たまねぎと小麦粉を混ぜて揚げる。終戦直後の食糧難で、イカも野菜くずも無駄にしないために生まれたという。いまは津軽の総菜売り場の定番。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ika_menchi_aomori.html',
    icon: { label: 'イカメンチ', shape: 'dumpling', baseColor: '#c98544', accentColor: '#e8d7a8' },
    reward: {
      unlockScore: 1000,
      area: '弘前市／鰺ヶ沢町',
      headline: '刺身の残りから生まれた',
      tip: '胴もエンペラも足もみじん切りにして使う。鰺ヶ沢や弘前など、地域ごとに売り出す動きが活発。',
      links: searchLinks('弘前市', 'イカメンチ'),
    },
  },
  {
    id: 'kaiyaki-miso',
    level: 4,
    name: '貝焼き味噌',
    shortName: '貝焼き味噌',
    image: '/assets/foods/aomori/04_kaiyaki_miso.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '直径20センチほどのホタテの貝殻を鍋にして直火にかける。煮干しとかつお節の出汁に味噌を溶き、板麩を入れて煮立て、最後に溶き卵でとじる。江戸時代から食べられていたといわれる漁師の料理。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kaiyaki_miso_aomori.html',
    icon: { label: '貝焼き味噌', shape: 'curry-plate', baseColor: '#e8b93c', accentColor: '#c9b79a' },
    reward: {
      unlockScore: 1800,
      area: 'むつ市／青森市／弘前市',
      headline: '貝殻がそのまま鍋になる',
      tip: '養殖が進んで大きな天然の貝殻が減ったため、漁家は大ぶりの貝殻を取っておく。太宰治が『津軽』でこの料理への郷愁を書いている。',
      links: searchLinks('むつ市', '貝焼き味噌'),
    },
  },
  {
    id: 'kuroishi-tsuyu-yakisoba',
    level: 5,
    name: '黒石つゆやきそば',
    shortName: '黒石つゆやきそば',
    image: '/assets/foods/aomori/05_kuroishi_tsuyu_yakisoba.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'モチモチした太い平麺をウスターソースで炒めた焼きそばに、たっぷりのつゆをかける。上に揚げ玉やねぎをのせる。天ぷらや卵をのせる店もある。',
    source:
      'https://www.umai-aomori.jp/local-cuisine/detail_102.html',
    icon: { label: 'つゆ焼そば', shape: 'noodle-bowl', baseColor: '#8a5a2b', accentColor: '#d9b45a' },
    reward: {
      unlockScore: 3000,
      area: '黒石市',
      headline: 'ソース焼きそばにつゆをかける',
      tip: '中学校前の店が、学校帰りの子どもたちに冷めた焼きそばに温かいそばつゆをかけて出したのが始まりとされる。近年はラーメンスープをかける店もある。',
      links: searchLinks('黒石市', '黒石つゆやきそば'),
      official: [{ label: '黒石市「黒石つゆやきそば伝紹会」', url: 'https://www.city.kuroishi.aomori.jp/kankou/spot/bussan/tsuyuyakisoba.html' }, { label: 'Amazing AOMORI（青森県観光情報サイト）「黒石つゆやきそば」', url: 'https://aomori-tourism.com/spot/detail_3628.html' }],
    },
  },
  {
    id: 'senbei-jiru',
    level: 6,
    name: 'せんべい汁',
    shortName: 'せんべい汁',
    image: '/assets/foods/aomori/06_senbei_jiru.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '鶏の出汁の醤油仕立ての汁に、南部せんべいを割って入れる。南部せんべいは小麦粉に塩と水を混ぜ、鉄の型で丸く焼いた保存食で、青森の南部地方と岩手の県北の主食だった。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/senbei_jiru_aomori.html',
    icon: { label: 'せんべい汁', shape: 'hotpot', baseColor: '#c8a15e', accentColor: '#7c4a22' },
    reward: {
      unlockScore: 4500,
      area: '八戸市',
      headline: 'せんべいを割って汁に入れる',
      tip: '戦中は鉄鍋も鉄型も供出されて作れなくなり、戦後に煮崩れしない専用のせんべいが開発された。いま八戸の約200店が出している。',
      links: searchLinks('八戸市', 'せんべい汁'),
    },
  },
  {
    id: 'miso-curry-milk-ramen',
    level: 7,
    name: '味噌カレー牛乳ラーメン',
    shortName: '味噌カレー牛乳',
    image: '/assets/foods/aomori/07_miso_curry_milk_ramen.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '味噌をベースにカレー粉と牛乳を合わせたスープのラーメン。バター、もやし、メンマ、わかめをのせる。青森市で生まれ、市民のソウルフードとされる。',
    source:
      'https://www.umai-aomori.jp/local-cuisine/detail_100.html',
    icon: { label: 'みそカレー', shape: 'ramen', baseColor: '#e0a640', accentColor: '#f3e3a0' },
    reward: {
      unlockScore: 6500,
      area: '青森市',
      headline: '味噌とカレーと牛乳のスープ',
      tip: '平成20年7月に「青森味噌カレー牛乳ラーメン普及会」ができ、翌年6月に協同組合になった。食品会社と組んで、家庭で食べられる商品も作られている。',
      links: searchLinks('青森市', '味噌カレー牛乳ラーメン'),
    },
  },
  {
    id: 'towada-barayaki',
    level: 8,
    name: '十和田バラ焼き',
    shortName: '十和田バラ焼き',
    image: '/assets/foods/aomori/08_towada_barayaki.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '牛のバラ肉と大量のたまねぎを、醤油ベースの甘辛いたれで味つけし、鉄板で焼く。豚バラや馬バラで作ることもある。三沢の米軍基地前の屋台が発祥といわれ、十和田市に広まって市民のソウルフードになった。',
    source:
      'https://www.umai-aomori.jp/local-cuisine/detail_101.html',
    icon: { label: 'バラ焼き', shape: 'hotpot', baseColor: '#7a4a2a', accentColor: '#d9a85a' },
    reward: {
      unlockScore: 9000,
      area: '十和田市',
      headline: '鉄板で牛バラとたまねぎ',
      tip: '平成20年12月、商店街や市、商工会議所青年部、学生などで「十和田バラ焼きゼミナール」が発足。ご当地グルメの北海道・東北大会で2連覇した。',
      links: searchLinks('十和田市', '十和田バラ焼き'),
    },
  },
  {
    id: 'hachinohe-maeoki-saba',
    level: 9,
    name: '八戸前沖さば（〆さば・焼き）',
    shortName: '八戸前沖さば',
    image: '/assets/foods/aomori/09_hachinohe_maeoki_saba.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '八戸市の東方約50km沖、本州最北のさば漁場で獲れ、八戸港に揚がるさば。脂が多く、身の締まった真サバはしめさばに、身のやわらかいゴマサバは焼き物に向く。',
    source:
      'https://www.umai-aomori.jp/specialtygoods-pbook/hachinohemaeoki-saba',
    icon: { label: '焼きさば', shape: 'fish', baseColor: '#5a7a96', accentColor: '#e8e2d0' },
    reward: {
      unlockScore: 12000,
      area: '八戸市',
      headline: '八戸沖の脂ののったさば',
      tip: '協議会が認めた期間に獲れたもので、脂肪分がおおむね15%以上が目安。秋が最盛期で、550g以上の大きなものは「銀鯖」として認定される。',
      links: searchLinks('八戸市', '八戸前沖さば'),
    },
  },
  {
    id: 'hiranai-hotate-katsugozen',
    level: 10,
    name: 'ホタテづくし膳（平内）',
    shortName: 'ホタテづくし膳',
    image: '/assets/foods/aomori/10_hiranai_hotate_katsugozen.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '活ホタテの刺身とステーキ、ベビーホタテの寿司と澄まし汁、ホタテアイスまで並ぶ、ホタテづくしの膳。刺身とステーキには平内産の3年もののホタテを使い、陸奥湾のホタテの甘みを味わう。平内町では「平内ホタテ活御膳」の名で出している。',
    source:
      'https://www.umai-aomori.jp/local-cuisine/detail_111.html',
    icon: { label: 'ホタテ', shape: 'sashimi-platter', baseColor: '#f0e6d2', accentColor: '#e88a4a' },
    reward: {
      unlockScore: 16000,
      area: '平内町',
      headline: '刺身からアイスまでホタテ',
      tip: '刺身はタテ切り・ヨコ切り・ナナメ切りの3通りに切り分け、切り方による食感の違いを食べ比べる。ステーキは焼き加減でも食感が変わる。',
      links: searchLinks('平内町', '平内ホタテ活御膳'),
      official: [{ label: '平内ホタテ料理推進協議会「食べられるお店」', url: 'https://www.hiranaihotate.com/restaurant/index.html' }],
    },
  },
  {
    id: 'ichigoni',
    level: 11,
    name: 'いちご煮',
    shortName: 'いちご煮',
    image: '/assets/foods/aomori/11_ichigoni.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '生ウニとアワビを煮た吸い物。アワビのエキスで乳白色に濁った汁に浮かぶ黄金色のウニが、朝露にかすむ野いちごのように見えたことから名がついた。八戸や階上の浜で漁師が作ったのが始まり。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ichigoni_aomori.html',
    icon: { label: 'いちご煮', shape: 'noodle-bowl', baseColor: '#f0e8dc', accentColor: '#e09a20' },
    reward: {
      unlockScore: 22000,
      area: '階上町／八戸市',
      headline: '野いちごに見えたから',
      tip: '大正時代に料亭で洗練され、1980年に地元業者が缶詰を作って全国に広まった。階上町は毎年7月にいちご煮まつりを開く。',
      links: searchLinks('八戸市', 'いちご煮'),
    },
  },
  {
    id: 'oma-maguro-don',
    level: 12,
    name: '大間まぐろ丼',
    shortName: '大間まぐろ丼',
    image: '/assets/foods/aomori/12_oma_maguro_don.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '大間崎の沖1〜3kmで一本釣りされるクロマグロを、町内の飲食店が寿司やマグロ丼にして出す。クロマグロはマグロの中でも最大級の種類で、大間では440kgの水揚げの記録がある。',
    source:
      'https://www.umai-aomori.jp/local-cuisine/detail_122.html',
    icon: { label: 'まぐろ丼', shape: 'rice-bowl', baseColor: '#b8283a', accentColor: '#f2a8b0' },
    reward: {
      unlockScore: 30000,
      area: '大間町',
      headline: '一本釣りのクロマグロの丼',
      tip: '大間町ではテグス1本で釣り上げる一本釣りが主流。津軽海峡で獲れ、大間漁協に水揚げされたものがブランドの大間マグロになる。',
      links: searchLinks('大間町', '大間 まぐろ丼'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const aomoriFoodStage: StageDefinition = {
  regionId: 'aomori',
  regionName: '青森県',
  mode: 'food',
  title: '青森めしマージ',
  theme: {
    primaryColor: '#c0334a',
    secondaryColor: '#f4e9e0',
    accentColor: '#e5b13c',
    backgroundColor: '#fdf6ef',
  },
  /** 背景ツアー「青森めぐり」。軸は季節。4月末の弘前公園の桜から始めて、2月の龍飛崎で終わる。雪解けから雪へ、という一年の順番。 */
  scenery: {
    tour: aomoriSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, AOMORI_FOOD_HULLS),
};
